/* eslint-disable @typescript-eslint/no-explicit-any */
import { RequiredLearningCounts } from '@app/notifications/notification-api.model';
import type { OrganizationSupportInfo } from '@degreed/lxp/org/models';
import { navigationToAspect } from '@degreed/lxp/org/utils';
import {
  BadgeRole,
  BrandingState,
  TrackerService,
  WebEnvironmentService,
} from '@degreed/lxp/services';
import { AuthUser } from '@degreed/lxp/services/authorization';
import { LayoutAspect, LayoutConfiguration } from '@degreed/lxp/ui';
import { TranslateService } from '@ngx-translate/core';
import {
  LxpAdminGuards,
  LxpLearnerGuards,
  resolveOrgInfo,
} from './permission.factory';
import { checkPermssionsFor } from './permission.guards';
import { LogTracker } from './tracker.utils';

const _lostKeys = new LogTracker('i18n warning');

/** Nav-config placeholder for the cross-app SSO hand-off URL — see `updateUrls()`. */
export const LEARN_IN_SSO_URL_TOKEN = '<learnInSSOUrl>';

export type NavOptions = {
  isProduction: boolean;
  authUser: AuthUser | undefined;
};
/**
 * For any `i18n` field, replace the associated field with translated value
 * @returns LayoutConfiguration
 */
export function updatei18n(
  configuration: LayoutConfiguration,
  translate: TranslateService,
  authUser?: AuthUser
): LayoutConfiguration {
  const MAPPINGS = {
    i18n: 'text',
    headerTitleI18n: 'headerTitle',
    buttonI18n: 'buttonText',
    titleI18n: 'titleText',
    badgeI18n: 'badgeI18n',
  };

  const KEY_REPLACEMENTS: Record<string, string> = {
    Core_SkillCoach: 'Core_MyTeam',
    AppHeader_SkillCoachTitle: 'Core_MyTeam',
    Core_Team: 'Core_MyTeam',
    dgUserProfile_SkillCoachLabel: 'Core_MyTeam',
    'global.navigation.learner.sidebar.skill-coach':
      'global.navigation.learner.sidebar.my-team',
  };

  const i18n = i18nWithLog(translate);
  const visitor = (node, key) => {
    // For each node, check if it has an translation field
    switch (key) {
      case 'i18n':
      case 'headerTitleI18n':
      case 'buttonI18n':
      case 'titleI18n':
      case 'badgeI18n':
        {
          const field = MAPPINGS[key];
          let i18nKey = node[key];

          if (i18nKey) {
            // Process comma-separated keys
            const keys = i18nKey.split(',').map((k) => k.trim());
            const newKeys: string[] = [];

            // Replace keys with their My Team equivalents, skip originals
            for (const k of keys) {
              if (KEY_REPLACEMENTS[k]) {
                // Use the My Team replacement instead
                newKeys.push(KEY_REPLACEMENTS[k]);
              } else if (!k || k.trim() === '') {
                // Skip empty keys
                continue;
              } else {
                // Keep keys that don't have replacements
                newKeys.push(k);
              }
            }

            i18nKey = newKeys.join(', ');
          }

          node[field] = i18n(i18nKey, node[field]);
        }
        break;
    }
  };

  configuration = traverse(configuration, visitor);

  // Warn if any i18n keys are missing from the localization bundle!
  _lostKeys.report();

  return configuration;
}

/**
 * Replace all `<orgId>` tokens with url-specific orgId or default orgId
 * This can be called at app initialization or at any time the user navigates to a new org
 *
 * @param configuration T = LayoutConfiguration | LayoutAspect
 * @param user AuthUser
 */
export function updateOrgId<T = LayoutConfiguration>(
  configuration: T,
  user: AuthUser | undefined,
  forceUpdate = false
): T {
  // Determine which org the user has access
  const [isOrgView, orgId] = resolveOrgInfo(user);
  if (isOrgView || forceUpdate) {
    const visitLinks = (node, key) => {
      switch (key) {
        case 'href':
        case 'routerLink':
          {
            const text: string = node[key] || '';
            if (text?.includes('<orgId>')) {
              node[key] = text.replace('<orgId>', orgId);
            }
          }
          break;
      }
    };

    // Only do this the url starts with `orgs/:id`
    configuration = traverse(configuration, visitLinks);
  }

  return configuration;
}

/**
 * For all navigation nodes, set `visible` to true unless explicitly set to false
 */
export function updateVisibility(
  configuration: LayoutConfiguration | LayoutAspect
): LayoutConfiguration | LayoutAspect {
  const isAspect = (
    configuration: LayoutConfiguration | LayoutAspect
  ): configuration is LayoutAspect =>
    !(configuration as LayoutConfiguration).admin &&
    !(configuration as LayoutConfiguration).learner;
  const visitNavigation = (node: LayoutNode) => {
    // Force explicity visibility settings
    if (node.visible !== false) node.visible = true;
  };

  if (isAspect(configuration)) {
    traverse(configuration.navigation, visitNavigation);
    traverse(configuration.features, visitNavigation);
  } else {
    traverse(configuration.admin, visitNavigation);
    traverse(configuration.learner, visitNavigation);
  }

  return configuration;
}

export function updateNavigationBranding(
  config: LayoutConfiguration,
  branding: Partial<BrandingState>,
  translate: TranslateService
): LayoutConfiguration {
  if (branding) {
    config.admin = config.admin
      ? navigationToAspect(branding.navigation, config.admin)
      : undefined;
    config.learner = config.learner
      ? navigationToAspect(branding.navigation, config.learner)
      : undefined;
    // set the alt text for the company graphic, if not set from branding then considered presentation and set to empty string
    config.admin.brand.mark.imageAltText =
      config.admin.brand.mark.altText || '';
    config.admin.brand.logo.imageAltText =
      config.admin.brand.logo.altText || '';
    config.learner.brand.mark.imageAltText =
      config.learner.brand.mark.altText || '';
    config.learner.brand.logo.imageAltText =
      config.learner.brand.logo.altText || '';
    // set the alt text for the logo as a link, determine what makes sense based on org or lxp routes
    config.admin.brand.logo.linkAltText = findAltTextForLogo(
      config.admin,
      translate
    );
    config.learner.brand.logo.linkAltText = findAltTextForLogo(
      config.learner,
      translate
    );
    config.admin.brand.mark.linkAltText = findAltTextForLogo(
      config.admin,
      translate
    );
    config.learner.brand.mark.linkAltText = findAltTextForLogo(
      config.learner,
      translate
    );
  }

  return config;
}

/**
 * Replace all `<user>` tokens with current user's vanityUrl
 * Replace profile nav item image with current user's picture
 * @param configuration LayoutConfiguration
 * @param user AuthUser
 */
export function updateUser(
  configuration: LayoutConfiguration,
  user: AuthUser | undefined,
  environment: WebEnvironmentService
): LayoutConfiguration {
  const hasProfile = !!user?.viewerProfile;
  const userName = user?.viewerProfile.vanityUrl || '';
  let profileImage = user?.viewerProfile.picture;

  const visitLinks = (node: LayoutNode, key: string) => {
    switch (key) {
      case 'href':
      case 'routerLink': {
        const text: string | undefined = node[key];
        if (text?.includes('<user>') && hasProfile)
          node[key] = text.replace('<user>', userName);
        if (text?.includes('<orgId>') && user?.defaultOrgId)
          node[key] = text.replace('<orgId>', user?.defaultOrgId.toString());
        break;
      }
    }
  };
  const visitDGATs = (node: LayoutNode) => {
    switch (node.dgat) {
      case 'global.navigation.learner.sidebar.profile': // Update Profile image
        if (hasProfile && profileImage) {
          if (profileImage.startsWith('~')) {
            profileImage = environment.getBlobUrl(profileImage);
            node.image = profileImage;
          }
        }
        break;
    }
  };

  configuration = traverse(configuration, visitLinks);
  configuration = traverse(configuration, visitDGATs);

  return configuration;
}

/**
 * Update the Degreed "Admin"  (instead of the Learner view) menu item visibility
 * NOTE: Some items require special access based on user permissions, org settings, and feature flags
 */
export function updateLxpAdminNavigation(
  configuration: LayoutConfiguration,
  guards: LxpAdminGuards,
  options: NavOptions
) {
  const [checkPermissions, showPermissionLog] = checkPermssionsFor(
    'LxP Admin',
    options.isProduction
  );

  const visitDGATs = (node: LayoutNode, key: string) => {
    if (key === 'dgat') {
      switch (node.dgat) {
        // Features:
        case 'global.navigation.admin.features.extended-enterprise':
          checkPermissions(node, [
            guards.features.child('extended-enterprise'),
          ]);
          break;

        // Insights (menu):
        case 'global.navigation.admin.sidebar.dashboard':
          checkPermissions(node, [guards.insights]);
          break;
        case 'global.navigation.admin.sidebar.dashboard.learning':
          checkPermissions(node, [guards.insights.child('learning')]);
          break;
        case 'global.navigation.admin.sidebar.dashboard.skills':
          checkPermissions(node, [guards.insights.child('skills')]);
          break;
        case 'global.navigation.admin.sidebar.dashboard.skill-trends':
          checkPermissions(node, [guards.insights.child('skill-trends')]);
          break;
        case 'global.navigation.admin.sidebar.dashboard.learning-trends':
          checkPermissions(node, [guards.insights.child('learning-trends')]);
          break;
        case 'global.navigation.admin.sidebar.dashboard.maestro-trends':
          checkPermissions(node, [guards.insights.child('maestro-trends')]);
          break;

        // People (menu):
        case 'global.navigation.admin.sidebar.people':
          checkPermissions(node, [guards.people]);
          break;
        case 'global.navigation.admin.sidebar.people.users':
          checkPermissions(node, [guards.people.child('users')]);
          break;
        case 'global.navigation.admin.sidebar.people.groups':
          checkPermissions(node, [guards.people.child('groups')]);
          break;
        case 'global.navigation.admin.sidebar.people.user-attributes':
          checkPermissions(node, [guards.people.child('manage-attributes')]);
          break;
        case 'global.navigation.admin.sidebar.people.permissions':
          checkPermissions(node, [guards.people.child('permissions')]);
          break;

        // Catalog (menu):
        case 'global.navigation.admin.sidebar.catalog':
          checkPermissions(node, [guards.catalog]);
          break;
        case 'global.navigation.admin.sidebar.catalog.content':
          checkPermissions(node, [guards.catalog.child('content')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.academies':
          checkPermissions(node, [guards.catalog.child('academies')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.pathways':
          checkPermissions(node, [guards.catalog.child('pathways')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.plans':
          checkPermissions(node, [guards.catalog.child('plans')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.flexible-pages':
          checkPermissions(node, [guards.catalog.child('flexiblePages')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.assessments':
          checkPermissions(node, [guards.catalog.child('assessments')]);
          break;
        case 'global.navigation.admin.sidebar.catalog.programs':
          checkPermissions(node, [guards.catalog.child('programs')]);
          break;

        // Skills (menu):
        case 'global.navigation.admin.sidebar.skills':
          checkPermissions(node, [guards.skills]);
          break;
        case 'global.navigation.admin.sidebar.skills.dashboard':
          checkPermissions(node, [guards.skills.child('dashboard')]);
          break;
        case 'global.navigation.admin.sidebar.skills.inventory':
          checkPermissions(node, [guards.skills.child('inventory')]);
          break;
        case 'global.navigation.admin.sidebar.skills.scales':
          checkPermissions(node, [guards.skills.child('scales')]);
          break;
        case 'global.navigation.admin.sidebar.skills.scaleslxp':
          checkPermissions(node, [guards.skills.child('scaleslxp')]);
          break;
        case 'global.navigation.admin.sidebar.skills.skill-list':
          checkPermissions(node, [guards.skills.child('org-skills')]);
          break;
        case 'global.navigation.admin.sidebar.skills.roles':
          checkPermissions(node, [guards.skills.child('roles')]);
          break;
        case 'global.navigation.admin.sidebar.skills.rolesskillsplus':
          checkPermissions(node, [guards.skills.child('rolesskillsplus')]);
          break;
        case 'global.navigation.admin.sidebar.skills.skill-standards':
          checkPermissions(node, [guards.skills.child('skill-standards')]);
          break;
        case 'global.navigation.admin.sidebar.skills.publish':
          checkPermissions(node, [guards.skills.child('publish')]);
          break;
        case 'global.navigation.admin.sidebar.skills.settings':
          checkPermissions(node, [guards.skills.child('settings')]);
          break;

        // Content MarketPlace (menu):
        case 'global.navigation.admin.sidebar.content-marketplace':
          checkPermissions(node, [guards.marketplace]);
          break;

        // Automation (menu):
        case 'global.navigation.admin.sidebar.automations':
          checkPermissions(node, [guards.automations]);
          break;

        // Reporting (menu):
        case 'global.navigation.admin.sidebar.reporting':
          checkPermissions(node, [guards.reporting]);
          break;
        case 'global.navigation.admin.sidebar.reporting.reports':
          checkPermissions(node, [guards.reporting.child('reports')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.presets':
          checkPermissions(node, [guards.reporting.child('presets')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.categories':
          checkPermissions(node, [guards.reporting.child('categories')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.ftp':
          checkPermissions(node, [guards.reporting.child('ftp')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.configurations':
          checkPermissions(node, [guards.reporting.child('configurations')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.segments':
          checkPermissions(node, [guards.reporting.child('segments')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.groups':
          checkPermissions(node, [guards.reporting.child('groups')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.advanced-analytics':
          checkPermissions(node, [guards.reporting.child('analytics')]);
          break;
        case 'global.navigation.admin.sidebar.reporting.self-service-analytics':
          checkPermissions(node, [
            guards.reporting.child('self-service-analytics'),
          ]);
          break;

        // Integrations (menu):
        case 'global.navigation.admin.sidebar.integrations':
          checkPermissions(node, [guards.integrations]);
          break;
        case 'global.navigation.admin.sidebar.integrations.connected':
          checkPermissions(node, [guards.integrations.child('connected')]);
          break;
        case 'global.navigation.admin.sidebar.integrations.directory':
          checkPermissions(node, [guards.integrations.child('directory')]);
          break;
        case 'global.navigation.admin.sidebar.integrations.api-keys':
          checkPermissions(node, [guards.integrations.child('api-keys')]);
          break;
        case 'global.navigation.admin.sidebar.integrations.file-upload':
          checkPermissions(node, [guards.integrations.child('file-upload')]);
          break;
        case 'global.navigation.admin.sidebar.integrations.file-log':
          checkPermissions(node, [guards.integrations.child('file-log')]);
          break;
        case 'global.navigation.admin.sidebar.integrations.webhooks':
          checkPermissions(node, [guards.integrations.child('webhooks')]);
          break;

        // Maestro coach builder (menu)
        case 'global.navigation.admin.sidebar.maestro':
          checkPermissions(node, [guards.maestro]);
          break;
        case 'global.navigation.admin.sidebar.maestro.experiences':
          checkPermissions(node, [guards.maestro.child('experiences')]);
          break;
        case 'global.navigation.admin.sidebar.maestro.settings':
          checkPermissions(node, [guards.maestro.child('settings')]);
          break;

        // Agents (menu):
        case 'global.navigation.admin.sidebar.agents':
          checkPermissions(node, [guards.agents]);
          break;
        case 'global.navigation.admin.sidebar.agents.overview':
          checkPermissions(node, [guards.agents.child('overview')]);
          break;
        case 'global.navigation.admin.sidebar.agents.settings':
          checkPermissions(node, [guards.agents.child('settings')]);
          break;

        // Settings (menu):
        case 'global.navigation.admin.sidebar.settings':
          checkPermissions(node, [guards.settings]);
          break;
        case 'global.navigation.admin.sidebar.settings.branding':
          checkPermissions(node, [guards.settings.child('branding')]);
          break;
        case 'global.navigation.admin.sidebar.settings.home':
          checkPermissions(node, [guards.settings.child('home')]);
          break;
        case 'global.navigation.admin.sidebar.settings.help-menu':
          checkPermissions(node, [guards.settings.child('help-menu')]);
          break;
        case 'global.navigation.admin.sidebar.settings.communication':
          checkPermissions(node, [guards.settings.child('communication')]);
          break;
        case 'global.navigation.admin.sidebar.settings.faq':
          checkPermissions(node, [guards.settings.child('faq')]);
          break;
        case 'global.navigation.admin.sidebar.settings.messaging':
          checkPermissions(node, [guards.settings.child('messaging')]);
          break;
        case 'global.navigation.admin.sidebar.settings.security':
          checkPermissions(node, [guards.settings.child('security')]);
          break;

        // Organizational Units (menu):
        case 'global.navigation.admin.sidebar.organizational-units':
          checkPermissions(node, [guards.organizationalUnits]);
          break;

        // Folders (menu):
        case 'global.navigation.admin.sidebar.folders':
          checkPermissions(node, [guards.folders]);
          break;

        // Extended Enterprise (menu):
        case 'global.navigation.admin.sidebar.extended-enterprise':
          checkPermissions(node, [guards.extendedEnterprise]);
          break;

        // Help (menu):
        case 'global.navigation.admin.sidebar.help':
        case 'global.navigation.admin.sidebar.help.knowledge-center':
        case 'global.navigation.admin.sidebar.help.privacy-policy':
        case 'global.navigation.admin.sidebar.help.cookie-notice':
        case 'global.navigation.admin.sidebar.help.faq':
        case 'global.navigation.admin.sidebar.help.custom-link':
        case 'global.navigation.admin.sidebar.help.support-phone':
        case 'global.navigation.admin.sidebar.help.support-email':
          break; // No guards
      }
    }
  };

  return showPermissionLog(traverse(configuration, visitDGATs));
}

/**
 * Update the navigation items based on feature flags and user permissions
 */
export function updateLxpLearnerNavigation(
  configuration: LayoutConfiguration,
  guards: LxpLearnerGuards,
  options: NavOptions
) {
  const [checkPermissions, showPermissionLog] = checkPermssionsFor(
    'LxP Learner',
    options.isProduction
  );

  const visitDGATs = (node, key) => {
    if (key === 'dgat') {
      switch (node.dgat) {
        // Features
        case 'global.navigation.learner.features.search':
          break;
        case 'global.navigation.learner.features.add-content':
          checkPermissions(node, [guards.features.child('add-content')]);
          break;
        case 'global.navigation.learner.features.beta':
          break;
        case 'global.navigation.learner.features.footer-branding':
          break;
        case 'global.navigation.learner.features.sidebar-branding':
          break;
        case 'global.navigation.learner.features.show-admin-view':
          checkPermissions(node, [guards.features.child('show-admin-view')]);
          break;
        case 'global.navigation.learner.features.extended-enterprise':
          checkPermissions(node, [
            guards.features.child('extended-enterprise'),
          ]);
          break;

        // Home (menu)
        case 'global.navigation.learner.sidebar.home':
          checkPermissions(node, [guards.home]);
          break;
        case 'global.navigation.learner.sidebar.home.overview':
          checkPermissions(node, [guards.home.child('overview')]);
          break;
        case 'global.navigation.learner.sidebar.home.assignments':
          checkPermissions(node, [guards.home.child('assignments')]);
          break;
        case 'global.navigation.learner.sidebar.home.saved':
          checkPermissions(node, [guards.home.child('saved')]);
          break;
        case 'global.navigation.learner.sidebar.home.shared':
          checkPermissions(node, [guards.home.child('shared')]);
          break;
        case 'global.navigation.learner.sidebar.home.pathways':
          checkPermissions(node, [guards.home.child('pathways')]);
          break;
        case 'global.navigation.learner.sidebar.home.plans':
          checkPermissions(node, [guards.home.child('plans')]);
          break;
        case 'global.navigation.learner.sidebar.home.groups':
          checkPermissions(node, [guards.home.child('groups')]);
          break;
        case 'global.navigation.learner.sidebar.home.pages':
          checkPermissions(node, [guards.home.child('pages')]);
          break;

        // Featured (menu)
        case 'global.navigation.learner.sidebar.featured':
          checkPermissions(node, [guards.featured]);
          break;

        // Discover (menu)
        case 'global.navigation.learner.sidebar.discover':
          checkPermissions(node, [guards.discover]);
          break;

        // Skill Coach(menu)
        case 'global.navigation.learner.sidebar.skill-coach':
          checkPermissions(node, [guards.skillCoach]);
          break;
        case 'global.navigation.learner.sidebar.skill-coach.members':
          checkPermissions(node, [guards.skillCoach.child('members')]);
          break;
        case 'global.navigation.learner.sidebar.skill-coach.team-skills':
          checkPermissions(node, [guards.skillCoach.child('team-skills')]);
          break;
        case 'global.navigation.learner.sidebar.skill-coach.assignments':
          checkPermissions(node, [guards.skillCoach.child('assignments')]);
          break;
        case 'global.navigation.learner.sidebar.skill-coach.learning-insights':
          checkPermissions(node, [
            guards.skillCoach.child('learning-insights'),
          ]);
          break;
        case 'global.navigation.learner.sidebar.skill-coach.skill-insights':
          checkPermissions(node, [guards.skillCoach.child('skill-insights')]);
          break;

        // Degreed Assistant (menu)
        case 'global.navigation.learner.sidebar.maestro':
          checkPermissions(node, [guards.maestro]);
          break;
        case 'global.navigation.learner.sidebar.maestro.quickactions':
          checkPermissions(node, [guards.maestro.child('quickactions')]);
          break;
        case 'global.navigation.learner.sidebar.maestro.askmaestro':
          checkPermissions(node, [guards.maestro.child('askmaestro')]);
          break;
        case 'global.navigation.learner.sidebar.maestro.newmaestro':
          checkPermissions(node, [guards.maestro.child('newmaestro')]);
          break;
        case 'global.navigation.learner.sidebar.maestro.browseexperiences':
          checkPermissions(node, [guards.maestro.child('experiences')]);
          break;

        // Opportunities (menu)
        case 'global.navigation.learner.sidebar.opportunities':
          checkPermissions(node, [guards.opportunities]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.marketplace':
          checkPermissions(node, [guards.opportunities.child('marketplace')]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.browse':
          checkPermissions(node, [guards.opportunities.child('browse')]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.mentoring':
          checkPermissions(node, [guards.opportunities.child('mentoring')]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.open-insights':
          checkPermissions(node, [guards.opportunities.child('open-insights')]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.engagement-insights':
          checkPermissions(node, [
            guards.opportunities.child('engagement-insights'),
          ]);
          break;
        case 'global.navigation.learner.sidebar.opportunities.job-positions':
          checkPermissions(node, [guards.opportunities.child('job-positions')]);
          break;

        // Notifications (menu)
        case 'global.navigation.learner.sidebar.notifications':
          checkPermissions(node, [guards.notifications]);
          break;

        // Profile (menu)
        case 'global.navigation.learner.sidebar.profile':
          checkPermissions(node, [guards.profile]);
          break;
        case 'global.navigation.learner.sidebar.profile.overview':
          checkPermissions(node, [guards.profile.child('overview')]);
          break;
        case 'global.navigation.learner.sidebar.profile.skills':
          checkPermissions(node, [guards.profile.child('skills')]);
          break;
        case 'global.navigation.learner.sidebar.profile.collection':
          checkPermissions(node, [guards.profile.child('collection')]);
          break;
        case 'global.navigation.learner.sidebar.profile.activity':
          checkPermissions(node, [guards.profile.child('activity')]);
          break;
        case 'global.navigation.learner.sidebar.profile.flex-ed':
          checkPermissions(node, [guards.profile.child('flex-ed')]);
          break;
        case 'global.navigation.learner.sidebar.profile.settings':
          checkPermissions(node, [guards.profile.child('settings')]);
          break;
        case 'global.navigation.learner.sidebar.profile.my-plan':
          checkPermissions(node, [guards.profile.child('my-plan')]);
          break;
        case 'global.navigation.learner.sidebar.profile.logout':
          checkPermissions(node, [guards.profile.child('logout')]);
          break;

        // Help (menu)
        case 'global.navigation.learner.sidebar.help':
          break;
        case 'global.navigation.learner.sidebar.help.knowledge-center':
          break;
        case 'global.navigation.learner.sidebar.help.privacy-policy':
          break;
        case 'global.navigation.learner.sidebar.help.cookie-notice':
          break;
        case 'global.navigation.learner.sidebar.help.faq':
          break;
        case 'global.navigation.learner.sidebar.help.custom-link':
          break;
        case 'global.navigation.learner.sidebar.help.support-phone':
          break;
        case 'global.navigation.learner.sidebar.help.support-email':
          break;
      }
    }
  };

  const visitLinks = (node, key) => {
    const userName = options.authUser?.viewerProfile.vanityUrl || '';
    const updateLink = (link) => (node.routerLink = link);

    if (key === 'dgat') {
      switch (node.dgat) {
        case 'global.navigation.learner.sidebar.home.overview':
          updateLink(`/${userName}/home/overview`);
          break;
        case 'global.navigation.learner.sidebar.home.overview-dashboard':
          updateLink(`/${userName}/dashboard/discover`);
          break;
        case 'global.navigation.learner.sidebar.home.assignments':
          updateLink(`/${userName}/home/assignments`);
          break;
      }
    }
  };

  return showPermissionLog(
    traverse(traverse(configuration, visitDGATs), visitLinks)
  );
}

/**
 * Update the navigation items with the correct URLs
 */
export function updateUrls(
  configuration: LayoutConfiguration,
  user: AuthUser,
  featuredPlanId: number,
  learnInSSOUrl: string,
  analyticsUrl: string
) {
  const showFeatured =
    featuredPlanId &&
    !user?.isSkillAnalyticsClient &&
    !user?.isSkillInventoryClient;

  const visitor = (node, key) => {
    const showNode = (isVisible: boolean | undefined) =>
      (node.visible = !!isVisible);

    const text = () => node[key];
    if (key === 'dgat') {
      switch (node.dgat) {
        case 'global.navigation.learner.sidebar.featured':
          showNode(showFeatured);
          break;
      }
    } else {
      switch (key) {
        // This 'text' replacement relies on i18n being updated beforehand
        case 'text':
          node[key] = text().replace(
            '{{orgName}}',
            user?.defaultOrgInfo?.name || 'LXP'
          );
          break;
        case 'routerLink':
        case 'href': {
          const rawUrl = text();
          // The SSO hand-off cannot carry an impersonation session. Must be
          // flagged before the `.replace()` below erases the token.
          if (
            typeof rawUrl === 'string' &&
            rawUrl.includes(LEARN_IN_SSO_URL_TOKEN)
          ) {
            node.blocksWhileImpersonating = true;
          }
          // these are only applicable to org users
          if (user?.defaultOrgId && node[key]) {
            node[key] = rawUrl
              .replace('<analyticsUrl>', analyticsUrl)
              .replace(LEARN_IN_SSO_URL_TOKEN, learnInSSOUrl)
              .replace('<featuredPlanId>', featuredPlanId.toString());
          }
          break;
        }
        case 'hrefBuilder':
          node.href = buildCustomHref(node[key]);
          break;
      }
    }
  };

  return traverse(configuration, visitor);
}

/**
 * If we need dynamic urls that are more complicated than can be handled by the replace
 * functionality then we can use this function.
 *
 * @param builderName The name of the custom builder to use
 * @returns
 */
export function buildCustomHref(builderName: string) {
  // Not doing an enum here because it's defined in the JSON file and not used anywhere else
  switch (builderName) {
    case 'INTEGRATIONS-PROVIDER_DIRECTORY_URL': {
      const testDirectoryUrl = 'https://degreed-beta.apideck.io';
      const prodDirectoryUrl = 'https://integrations.degreed.com';
      const useTestDirectoryUrl: boolean = new URLSearchParams(
        window.location.search
      ).has('useTestIntegrationsDirectory');
      return (
        (useTestDirectoryUrl ? testDirectoryUrl : prodDirectoryUrl) +
        '?host=' +
        encodeURIComponent(window.location.host)
      );
    }
  }
}

/** In-app routes for the standalone legal pages (`libs/lxp/features/legal`). */
const PRIVACY_POLICY_ROUTE = '/privacy-policy';
const COOKIE_NOTICE_ROUTE = '/cookie-notice';

/** `?newDocs=true` previews the in-app legal pages; read once at layout build, so a later URL change does not retarget the links. */
export function readNewDocsFlag(): boolean {
  return new URLSearchParams(window.location.search).get('newDocs') === 'true';
}

export function updateNotificationMenuItem(
  configuration: LayoutConfiguration,
  notificationCounts: RequiredLearningCounts
): LayoutConfiguration {
  const visitor = (node) => {
    if (node.badge === BadgeRole.notification) {
      node.badgeVisible = notificationCounts.notificationCount > 0;
    }
  };
  return traverse(configuration, visitor);
}

/**
 * config = updateHelpMenu(config, organizationId, helpMenu)
 * Update the help menu items based on the organization's support info
 */
export function updateHelpMenu(
  configuration: LayoutConfiguration | LayoutAspect,
  organizationId: number,
  supportInfo: OrganizationSupportInfo,
  useNewDocs = readNewDocsFlag()
) {
  if (!organizationId) supportInfo = { phone: '800.311.7061' };

  const visitDGATs = (node, key) => {
    const updateByKey = (field, value, allowEmpty = false) => {
      const isVisible = value || allowEmpty;

      node.visible = isVisible;
      if (isVisible) node[field] = value || '';
    };
    // Also drop the backend href/target: layout.utils active matching and dgx-nav-popover prefer `href` over `routerLink`.
    const setInAppRoute = (routerLink: string) => {
      if (!useNewDocs) return;
      node.routerLink = routerLink;
      delete node.href;
      delete node.target;
    };
    if (key === 'dgat') {
      switch (node.dgat) {
        case 'global.navigation.admin.sidebar.help.knowledge-center':
        case 'global.navigation.learner.sidebar.help.knowledge-center':
          updateByKey('href', supportInfo?.helpLink);
          node.blocksWhileImpersonating = true;
          break;
        case 'global.navigation.admin.sidebar.help.faq':
        case 'global.navigation.learner.sidebar.help.faq':
          updateByKey('href', supportInfo?.faq);
          break;
        case 'global.navigation.admin.sidebar.help.cookie-notice':
        case 'global.navigation.learner.sidebar.help.cookie-notice':
          node.visible = supportInfo?.showCookieLink;
          setInAppRoute(COOKIE_NOTICE_ROUTE);
          break;
        case 'global.navigation.admin.sidebar.help.privacy-policy':
        case 'global.navigation.learner.sidebar.help.privacy-policy':
          node.visible = !supportInfo?.disableDegreedPrivacyPolicy;
          setInAppRoute(PRIVACY_POLICY_ROUTE);
          break;

        case 'global.navigation.admin.sidebar.help.custom-link':
        case 'global.navigation.learner.sidebar.help.custom-link':
          updateByKey('text', supportInfo?.customText);
          updateByKey('href', supportInfo?.customLink);
          break;
        case 'global.navigation.admin.sidebar.help.support-phone':
        case 'global.navigation.learner.sidebar.help.support-phone':
          updateByKey('text', supportInfo?.phone);
          updateByKey(
            'href',
            supportInfo?.phone ? `tel:${supportInfo.phone}` : ''
          );
          break;
        case 'global.navigation.admin.sidebar.help.support-email':
        case 'global.navigation.learner.sidebar.help.support-email':
          {
            const hasEmail =
              supportInfo?.email &&
              !supportInfo?.email.includes('@degreed.com');
            updateByKey('text', hasEmail ? supportInfo.email : '');
            updateByKey('href', hasEmail ? `mailto:${supportInfo.email}` : '');
          }
          break;
      }
    }
  };

  return traverse(configuration, visitDGATs);
}

/**
 * Add analytics tracking to report clicks on the navigation items
 * @returns
 */
export function addAnalyticTrackers(
  configuration: LayoutConfiguration,
  trackerService: TrackerService
): LayoutConfiguration {
  // Build analytics data for tracking
  const makeTrackData = (key: string) => ({
    category: '',
    label: '',
    action: 'Global Navigation Item Clicked',
    properties: { itemClicked: key },
  });
  const visitor = (node, key) => {
    switch (key) {
      case 'analytics':
        {
          const itemId = node.analytics;
          const defaultVal = node.text || '';

          // Inject callback tracking function
          node.trackEvent = () => {
            if (!itemId)
              console.error(
                `Missing analytics key: ${itemId} for ${defaultVal} (${node.i18n || ''})`
              );

            trackerService.trackEventData(makeTrackData(itemId));
          };
        }
        break;
    }
  };

  return traverse(configuration, visitor);
}

/**
 * For screen readers, calculate the alt-text for the logo link [in the upper left corner of the nav bar]
 */
export const findAltTextForLogo = (
  layout: LayoutAspect,
  translate: TranslateService
): string => {
  const firstVisibleTopNavItem = (): string => {
    const visibleNavItems = layout.navigation.top.filter(
      (navItem) => navItem.visible
    );
    return visibleNavItems[0].text;
  };
  const pathname = location.pathname;
  const isOrgView = pathname.indexOf('orgs') !== -1;
  const isOnboardingPage = pathname.indexOf('getstarted/welcome') !== -1;
  const key = isOnboardingPage ? 'Onboarding_SectionTitle' : 'Core_Home';

  return isOrgView ? firstVisibleTopNavItem() : translate.instant(key);
};

/**
 * Translation util that logs missing translations
 * Keys can be comma-separated to try multiple keys until a valid translation is found
 * Note: Left-most key is the most recent... right-most key is the fallback
 */
export const i18nWithLog =
  (translate: TranslateService) =>
  (key: string, fallback: string, params?: Record<string, unknown>) => {
    const allKeys = key.replace(/\s+/g, '').split(','); // This will remove all whitespace

    // Find "most recent" translation
    let value = '';
    let selectedKey = '';

    for (let i = 0; i < allKeys.length; i++) {
      if (!value && allKeys[i]) {
        selectedKey = allKeys[i];
        value = translate.instant(selectedKey, params) || '';

        // If ngx/translate returns the key, it means the translation is missing
        if (value === selectedKey) value = '';
      }

      if (!value) _lostKeys.track(selectedKey, fallback);
    }

    return value || fallback;
  };

// *************************************************
// Tree Utils
// *************************************************

type LayoutNode = Record<string, any>;
type Visitor = (node: any, key: string, parent?: any) => void;

// Depth-first node tree scanning
const traverse = (node: any, visit: Visitor, parent?: LayoutNode) => {
  for (const key in node) {
    if (typeof node[key] === 'object') {
      visit(node, key, parent);
      traverse(node[key], visit, node);
    } else {
      visit(node, key, parent);
    }
  }
  return node;
};
